import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import ListItem from "./ListItem";
import { Link } from "react-router-dom";
import UserImg from "../../../assets/images/userImg.png";
import AnnouncementModalItem from "../../../pages/tutoring/Dashboard/AnnouncementModalItem";
import { XIcon } from "@heroicons/react/outline";
import { useAxios } from "../../../hooks";
import TaskItem from "../../../pages/tutoring/Dashboard/TaskItem";

export default function TutorSidebar({ navigation, user }) {
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [readAnnouncementIds, setReadAnnouncementIds] = useState([]);
  const [activeAnnouncementId] = useState(null);
  const [actionItems, setActionItems] = useState([]);
  const [markingAsRead, setMarkingAsRead] = useState({});

  const completedItems = useMemo(() => actionItems?.filter((task, index) => task["Completed"]) || [], [actionItems]);
  const uncompletedItems = useMemo(() => actionItems?.filter((task, index) => !task["Completed"]) || [], [actionItems]);
  const modalItemRefs = useRef({});
  const { axiosInstance, idToken } = useAxios();

  // Calculate counts for unread announcements and uncompleted tasks
  const unreadAnnouncementsCount = useMemo(() => {
    if (!announcements?.length || !readAnnouncementIds) return 0;
    return announcements.filter(announcement => !readAnnouncementIds.includes(announcement.id)).length;
  }, [announcements, readAnnouncementIds]);

  const uncompletedTasksCount = useMemo(() => {
    return uncompletedItems?.length || 0;
  }, [uncompletedItems]);

  // Recursively enhance navigation
  const enhanceNavigation = (items) => {
    return items.map((item) => {
      const isUpdates = item.name === "Updates";
      const isTasks = item.name === "Tasks";
      
      let count = 0;
      if (isUpdates) {
        count = unreadAnnouncementsCount;
      } else if (isTasks) {
        count = uncompletedTasksCount;
      }

      const onClick = isUpdates
        ? (e) => {
            e.preventDefault();
            setShowAnnouncementModal(true);
          }
        : isTasks
        ? (e) => {
            e.preventDefault();
            setShowTaskModal(true);
          }
        : undefined;

      return {
        ...item,
        onClick,
        count,
        children: item.children ? enhanceNavigation(item.children) : [],
      };
    });
  };

  const enhancedNavigation = enhanceNavigation(navigation);

  const getAnnouncements = useCallback(async () => {
    if (!axiosInstance?.get) return { error: "Axios not ready" };

    return axiosInstance
      .get("/portal-announcement")
      .then(({ data }) => {
        setAnnouncements(data.announcements);
        setReadAnnouncementIds(data.readAnnouncementIds);
        return { ok: true, data };
      })
      .catch((error) => {
        return { error };
      });
  }, [axiosInstance]);

  useEffect(() => {
    if (!idToken || !axiosInstance?.get) return;

    (async () => {
      await getAnnouncements();
    })();
  }, [getAnnouncements, axiosInstance, idToken]);

  const getActionItems = useCallback(() => {
    if (!axiosInstance?.get) return { error: "Axios not ready" };

    return axiosInstance
      .get("/action-item")
      .then(({ data }) => {
        // Ensure data is an array before setting it
        setActionItems(Array.isArray(data) ? data : []);
      })
      .catch((error) => {
        console.error("Error fetching action items:", error);
        setActionItems([]); // Set empty array on error
        return { error };
      });
  }, [axiosInstance]);

  useEffect(() => {
    if (!idToken || !axiosInstance?.get) return;

    (async () => {
      await getActionItems();
    })();
  }, [getActionItems, axiosInstance, idToken]);

  const completeItem = (actionItemId) => {
    const updatedActionItems = actionItems.map((item) => {
      if (item["Action_Item_ID"] === actionItemId) {
        return { ...item, Completed: true };
      }
      return item;
    });
    setActionItems(updatedActionItems);
  };

  const markAsRead = useCallback(async (announcementId) => {
    if (!axiosInstance?.post) return;

    setMarkingAsRead(prev => ({ ...prev, [announcementId]: true }));

    try {
      await axiosInstance.post("/portal-announcement/status", {
        announcementId
      });

      // Update the readAnnouncementIds state to include the newly read announcement
      setReadAnnouncementIds(prev => [...prev, announcementId]);
    } catch (error) {
      console.error("Error marking announcement as read:", error);
    } finally {
      setMarkingAsRead(prev => ({ ...prev, [announcementId]: false }));
    }
  }, [axiosInstance]);

  return (
    <>
      <div className="flex flex-col justify-between">
        <div className="pb-4 overflow-y-auto sideNav flex flex-col gap-2 p-5">
          {enhancedNavigation.map((item, index) => (
            <ListItem key={index} {...item} />
          ))}
        </div>

        {user && (
          <div className="p-5">
            <Link
              to="/logout"
              className="text-titleColor cursor-pointer group flex gap-2 rounded-md items-center w-full px-2 py-2 hover:bg-greyHover">
              <img src={UserImg} className="w-7 h-7 rounded-full border border-[#E5E7EB]" alt="user-img" />{" "}
              <span className="text-[#E6E6E6]">|</span>
              <span className="text-titleColor text-sm">Log Out</span>
            </Link>
          </div>
        )}
      </div>

      {showAnnouncementModal && createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[99999]"
          onClick={() => setShowAnnouncementModal(false)}>
          <div
            className="relative bg-white rounded-3xl shadow-lg w-full max-w-2xl transform transition-all duration-300 p-6 md:p-10 h-[80vh] overflow-auto z-[100000]"
            onClick={(e) => e.stopPropagation()}>
            <div className="text-lg md:text-3xl font-bold font-inter pb-6 text-[#0c6980]">Updates</div>
            <button onClick={() => setShowAnnouncementModal(false)} className="absolute top-5 right-5 text-black">
              <XIcon className="w-5 h-5" />
            </button>
            <div className="pb-5 w-full">
              {announcements?.length > 0 ? (
                announcements.map((announcement, index) => (
                  <AnnouncementModalItem
                    key={index}
                    {...announcement}
                    ref={(el) => (modalItemRefs.current[announcement.id] = el)}
                    activeAnnouncementId={activeAnnouncementId}
                    isRead={readAnnouncementIds.includes(announcement.id)}
                    isLoading={markingAsRead[announcement.id] || false}
                    markAsRead={markAsRead}
                  />
                ))
              ) : (
                <div className="text-xs text-[#1F2937] overflow-hidden text-ellipsis whitespace-pre-wrap line-clamp-2">
                  No Available Updates
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {showTaskModal && createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[99999]"
          onClick={() => setShowTaskModal(false)}>
          <div
            className="relative bg-white rounded-3xl shadow-lg w-full max-w-2xl transform transition-all duration-300 p-6 md:p-10 h-[80vh] overflow-auto z-[100000]"
            onClick={(e) => e.stopPropagation()}>
            <div className="w-full">
              <div className="text-lg md:text-3xl font-bold font-inter pb-6 text-[#0c6980]">Tasks</div>
              <button onClick={() => setShowTaskModal(false)} className="absolute top-5 right-5 text-black">
                <XIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="w-full overflow-auto">
                {actionItems?.length > 0 ? (
                  [...uncompletedItems, ...completedItems]?.map((task, index) => (
                    <TaskItem key={index} {...task} completeItem={completeItem} />
                  ))
                ) : (
                  <div className="text-xs text-[#1F2937] overflow-hidden text-ellipsis whitespace-pre-wrap">
                    No Available Items
                  </div>
                )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
